import { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

/* eslint-disable */

const AuthContext = createContext();
const api_url = process.env.REACT_APP_API_URL;

export function useAuth() {
  return useContext(AuthContext);
}

const axiosInstance = axios.create({
    baseURL: api_url,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [membership, setMembership] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [accessToken, setAccessToken] = useState(localStorage.getItem("accessToken"));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem("refreshToken"));

  const navigate = useNavigate();

  useEffect(() => {
    const savedAccess = localStorage.getItem("accessToken");
    const savedRefresh = localStorage.getItem("refreshToken");

    if (savedAccess && savedRefresh) {
      setAccessToken(savedAccess);
      setRefreshToken(savedRefresh);

      fetchCurrentUser();

    } else {
      console.info("No tokens found.")
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await axiosInstance.get('/accounts/get-user/', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        withCredentials: true,
      });

      const firstMembership = res.data.memberships?.[0];
      const company = firstMembership?.company;

      setUser(res.data);
      setMembership(firstMembership ?? null);
      setDashboardData(res.data.dashboard_data);

      if (company?.id) localStorage.setItem('companyId', company.id);
    } catch (err) {
      console.error('Failed to fetch user', err);
    }
  };

  const login = async (username, password) => {
    try {
        const res = await axiosInstance.post('/accounts/token/', { username, password });
      
      setAccessToken(res.data.access);
      setRefreshToken(res.data.refresh);
      localStorage.setItem('accessToken', res.data.access);
      localStorage.setItem('refreshToken', res.data.refresh);
      await fetchCurrentUser();
      return true;
   
    } catch (err) {
      console.error('Login failed', err);
      return false;
    }
  };


  // Attach interceptor for token refresh
  useEffect(() => {
    // Always attach freshest token to requests
    const reqInterceptor = axiosInstance.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      const companyId = localStorage.getItem('companyId');
      if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      if  (companyId) {
       config.headers['X-Company-ID'] = companyId;
      }
      return config;
    });

    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const res = await axiosInstance.post('/accounts/token/refresh/', {
              refresh:
                refreshToken,
            });

            const newAccessToken = res.data.access;
            setAccessToken(newAccessToken);
            localStorage.setItem("accessToken", newAccessToken);

            originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return axiosInstance(originalRequest);

          } catch (err) {
            logout();
            return Promise.reject(err);
          }
        }

        return Promise.reject(error);
      }
    );
    return () => {
      axiosInstance.interceptors.request.eject(reqInterceptor);
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [refreshToken]);


  const logout = async () => {    
    const response = await axiosInstance.post('/accounts/logout/', {}, {
      headers: { Authorization: `Bearer ${accessToken}` },
      withCredentials: true,
    });

    if (response.status === 200) {

      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');


    } else {

      console.error("Logout failed", response);
    }

    navigate('/signin', { replace: true });
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, membership, dashboardData, accessToken, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export default axiosInstance;
/* eslint-enable */

